import { useState, useMemo, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingDown, TrendingUp, Minus, BarChart3, Building2, MapPin } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";

export default function Benchmark() {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState<string>("2024");
  const [selectedSector, setSelectedSector] = useState<string>("SERV");
  const [selectedSize, setSelectedSize] = useState<string>("PME");

  // Fetch user's company data
  const { data: user } = trpc.auth.me.useQuery();
  const { data: company } = trpc.company.get.useQuery(
    undefined,
    { enabled: !!user?.company_id }
  );

  // Fetch company's ADEME sector code
  const { data: ademeSector } = trpc.company.getAdemeSector.useQuery(
    undefined,
    { enabled: !!user?.company_id }
  );

  // Initialize filters with company data
  useEffect(() => {
    if (ademeSector && selectedSector === 'SERV') {
      setSelectedSector(ademeSector);
    }
  }, [ademeSector]);

  useEffect(() => {
    if (company?.company_size && selectedSize === 'PME') {
      // If TPE, use PME as fallback (no TPE data in ADEME)
      const size = company.company_size === 'TPE' ? 'PME' : company.company_size;
      setSelectedSize(size);
    }
  }, [company]);

  // Fetch benchmark data
  const { data: benchmarkData, isLoading } = trpc.benchmark.getSectorBenchmark.useQuery({
    sector_code: selectedSector || 'SERV',
    company_size: selectedSize || 'PME',
    region_code: "FR",
    year: parseInt(selectedYear),
  }, {
    enabled: !!selectedSector && !!selectedSize,
  });

  // Calculate user's total emissions (from periods)
  const { data: periods } = trpc.periods.list.useQuery();
  
  console.log('[Benchmark] Periods data:', periods);
  
  // Get the first period (which contains the most data)
  const latestPeriod = useMemo(() => {
    if (!periods || periods.length === 0) {
      console.log('[Benchmark] No periods available');
      return null;
    }
    const latest = periods[0];
    console.log('[Benchmark] Using period:', latest);
    return latest;
  }, [periods]);
  
  const { data: emissions } = trpc.calculator.listByPeriod.useQuery(
    { periodId: latestPeriod?.id || "" },
    { enabled: !!latestPeriod?.id }
  );

  const userTotalEmissions = useMemo(() => {
    console.log('[Benchmark] Calculating emissions:', { emissions, length: emissions?.length });
    if (!emissions || emissions.length === 0) {
      console.log('[Benchmark] No emissions data');
      return 0;
    }
    const total = emissions.reduce((sum, e) => sum + (e.tco2e || 0), 0);
    console.log('[Benchmark] Total emissions:', total);
    return total;
  }, [emissions]);

  // Calculate comparison
  const comparison = useMemo(() => {
    console.log('[Benchmark] Calculating comparison:', { benchmarkData, userTotalEmissions });
    if (!benchmarkData || !userTotalEmissions) {
      console.log('[Benchmark] Missing data for comparison:', { hasBenchmark: !!benchmarkData, hasEmissions: !!userTotalEmissions });
      return null;
    }

    const reference = benchmarkData.percentile_50 || benchmarkData.avg_total_emissions;
    const difference = ((userTotalEmissions - reference) / reference) * 100;

    return {
      reference,
      difference,
      percentile: calculatePercentile(userTotalEmissions, benchmarkData),
    };
  }, [benchmarkData, userTotalEmissions]);

  function calculatePercentile(value: number, benchmark: any): number {
    if (!benchmark) return 50;
    
    if (value <= (benchmark.percentile_25 || 0)) return 25;
    if (value <= (benchmark.percentile_50 || 0)) return 50;
    if (value <= (benchmark.percentile_75 || 0)) return 75;
    if (value <= (benchmark.percentile_90 || 0)) return 90;
    return 95;
  }

  function getTrendIcon(diff: number) {
    if (diff < -5) return <TrendingDown className="h-5 w-5 text-green-600" />;
    if (diff > 5) return <TrendingUp className="h-5 w-5 text-red-600" />;
    return <Minus className="h-5 w-5 text-yellow-600" />;
  }

  function getDifferenceColor(diff: number): string {
    if (diff < -5) return "text-green-600";
    if (diff > 5) return "text-red-600";
    return "text-yellow-600";
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('benchmark.title')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('benchmark.subtitle')}
          </p>
        </div>

        {/* TPE Notice */}
        {company?.company_size === 'TPE' && selectedSize === 'PME' && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <p className="text-sm text-blue-900">
                ℹ️ {t('benchmark.tpe_notice')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>{t('benchmark.filters_title')}</CardTitle>
            <CardDescription>
              {t('benchmark.filters_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('benchmark.year')}</label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2023">2023</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t('benchmark.sector')}</label>
                <Select value={selectedSector} onValueChange={setSelectedSector}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AGRI">Agriculture</SelectItem>
                    <SelectItem value="INDU">Industrie</SelectItem>
                    <SelectItem value="SERV">Services</SelectItem>
                    <SelectItem value="ENER">Énergie</SelectItem>
                    <SelectItem value="TRAN">Transport</SelectItem>
                    <SelectItem value="BUIL">Bâtiments</SelectItem>
                    <SelectItem value="WAST">Déchets</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">{t('benchmark.company_size')}</label>
                <Select value={selectedSize} onValueChange={setSelectedSize}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TPE">TPE (&lt;10)</SelectItem>
                    <SelectItem value="PME">PME (10-250)</SelectItem>
                    <SelectItem value="ETI">ETI (250-5000)</SelectItem>
                    <SelectItem value="GE">GE (&gt;5000)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Comparison Card */}
        {isLoading ? (
          <Card>
            <CardContent className="pt-6">
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        ) : comparison ? (
          <Card className="border-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl">{t('benchmark.your_performance')}</CardTitle>
                  <CardDescription>
                    {t('benchmark.comparison_with', { count: benchmarkData?.sample_size || 0 })}
                  </CardDescription>
                </div>
                {comparison.difference !== 0 && (
                  <Badge
                    variant={comparison.difference < 0 ? "default" : "destructive"}
                    className="text-lg px-4 py-2"
                  >
                    {comparison.difference > 0 ? "+" : ""}
                    {comparison.difference.toFixed(1)}%
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Emissions Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="h-4 w-4" />
                    <span>{t('benchmark.your_emissions')}</span>
                  </div>
                  <div className="text-3xl font-bold">
                    {userTotalEmissions.toFixed(2)}
                    <span className="text-lg text-muted-foreground ml-2">t CO₂e</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <BarChart3 className="h-4 w-4" />
                    <span>{t('benchmark.reference_median')}</span>
                  </div>
                  <div className="text-3xl font-bold">
                    {comparison.reference.toFixed(2)}
                    <span className="text-lg text-muted-foreground ml-2">t CO₂e</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {getTrendIcon(comparison.difference)}
                    <span>{t('benchmark.difference')}</span>
                  </div>
                  <div className={`text-3xl font-bold ${getDifferenceColor(comparison.difference)}`}>
                    {comparison.difference > 0 ? "+" : ""}
                    {comparison.difference.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Percentile Position */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('benchmark.your_position')}</span>
                  <span className="font-medium">
                    {t('benchmark.percentile', { value: comparison.percentile })}
                  </span>
                </div>
                <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`absolute h-full transition-all ${
                      comparison.percentile <= 50 ? "bg-green-500" : "bg-red-500"
                    }`}
                    style={{ width: `${comparison.percentile}%` }}
                  />
                  <div
                    className="absolute h-full w-1 bg-foreground"
                    style={{ left: `${comparison.percentile}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Vous émettez {comparison.percentile <= 50 ? "moins" : "plus"} que{" "}
                  {comparison.percentile}% des entreprises similaires
                </p>
              </div>

              {/* Scope Breakdown */}
              {benchmarkData && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                  <div className="space-y-1">
                    <div className="text-sm text-muted-foreground">Scope 1</div>
                    <div className="text-lg font-semibold">
                      Moy: {benchmarkData.avg_scope1?.toFixed(2) || "N/A"} t CO₂e
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm text-muted-foreground">Scope 2</div>
                    <div className="text-lg font-semibold">
                      Moy: {benchmarkData.avg_scope2?.toFixed(2) || "N/A"} t CO₂e
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-sm text-muted-foreground">Scope 3</div>
                    <div className="text-lg font-semibold">
                      Moy: {benchmarkData.avg_scope3?.toFixed(2) || "N/A"} t CO₂e
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucune donnée disponible</h3>
                <p className="text-sm text-muted-foreground">
                  Aucun benchmark disponible pour les critères sélectionnés
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Distribution Card */}
        {benchmarkData && (
          <Card>
            <CardHeader>
              <CardTitle>Distribution des émissions</CardTitle>
              <CardDescription>
                Répartition des émissions dans votre secteur
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">25e percentile</div>
                    <div className="text-lg font-semibold">
                      {benchmarkData.percentile_25?.toFixed(2) || "N/A"}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">50e percentile (médiane)</div>
                    <div className="text-lg font-semibold">
                      {benchmarkData.percentile_50?.toFixed(2) || "N/A"}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">75e percentile</div>
                    <div className="text-lg font-semibold">
                      {benchmarkData.percentile_75?.toFixed(2) || "N/A"}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground">90e percentile</div>
                    <div className="text-lg font-semibold">
                      {benchmarkData.percentile_90?.toFixed(2) || "N/A"}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <MapPin className="h-4 w-4" />
                    <span>Source des données</span>
                  </div>
                  <p className="text-sm">
                    Base ADEME - {benchmarkData.sample_size} entreprises analysées
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
